<template>
    <div class="onboarding-tracking">
        <q-markup-table>
            <template v-slot:default>
                <thead>
                    <tr>
                        <th class="text-left">
                            {{ $t("onboarding.publisher") }}
                        </th>
                        <th class="text-left">{{ $t("onboarding.status") }}</th>
                        <th class="text-left">
                            {{ $t("onboarding.details") }}
                        </th>
                        <th class="text-right">
                            {{ $t("onboarding.actions") }}
                        </th>
                    </tr>
                </thead>
                <tbody>
                    <tr v-for="pub in localPublishers" :key="pub.userId">
                        <td class="text-left font-weight-bold">
                            {{ pub.name }}
                        </td>
                        <td class="text-left">
                            <q-badge v-if="pub.totalSteps === 0" color="grey">{{
                                $t("onboarding.no_steps")
                            }}</q-badge>
                            <q-badge
                                v-else-if="pub.isFinished"
                                color="positive"
                                >{{ $t("onboarding.completed") }}</q-badge
                            >
                            <q-badge v-else-if="pub.hasStarted" color="warning">
                                {{ $t("onboarding.in_progress") }} ({{
                                    pub.completedCount
                                }}/{{ pub.totalSteps }})
                            </q-badge>
                            <q-badge v-else color="negative">{{
                                $t("onboarding.not_started")
                            }}</q-badge>
                            <div
                                v-if="pub.isFinished && pub.finishedAt"
                                class="text-caption text-grey-7 q-mt-xs"
                            >
                                {{ $t("onboarding.completed_at") }}:
                                {{ pub.finishedAt }}
                            </div>
                        </td>
                        <td class="text-left">
                            <ul
                                v-if="
                                    pub.progressList &&
                                    pub.progressList.length > 0
                                "
                                style="margin: 0; padding-left: 20px"
                            >
                                <li
                                    v-for="p in pub.progressList"
                                    :key="p.step_id"
                                >
                                    {{ p.step_title }} -
                                    <strong>{{
                                        $t("onboarding." + p.status)
                                    }}</strong>
                                    <span
                                        v-if="
                                            p.status === 'pending' &&
                                            p.step_type === 'confirmation'
                                        "
                                        class="text-warning"
                                    >
                                        ({{ $t("onboarding.requires_action") }})
                                    </span>
                                </li>
                            </ul>
                            <span v-else>-</span>
                        </td>
                        <td class="text-right">
                            <div
                                class="row justify-end items-center q-gutter-xs"
                            >
                                <q-btn
                                    v-if="pub.pendingConfirmationStep"
                                    size="sm"
                                    color="secondary"
                                    icon="check_circle"
                                    :label="$t('onboarding.confirm_step')"
                                    @click="openConfirmModal(pub)"
                                />
                                <form
                                    v-if="!pub.isFinished && pub.totalSteps > 0"
                                    :action="getFinishUrl(pub.userId)"
                                    method="POST"
                                    style="display: inline"
                                >
                                    <input
                                        type="hidden"
                                        name="_token"
                                        :value="csrfToken"
                                    />
                                    <q-btn
                                        type="submit"
                                        size="sm"
                                        color="primary"
                                        :label="$t('onboarding.mark_completed')"
                                    />
                                </form>
                                <q-btn
                                    v-if="
                                        pub.hasStarted ||
                                        pub.isFinished ||
                                        pub.completedCount > 0
                                    "
                                    size="sm"
                                    color="negative"
                                    flat
                                    icon="restart_alt"
                                    :label="$t('onboarding.reset')"
                                    @click="openResetModal(pub)"
                                />
                            </div>
                        </td>
                    </tr>
                </tbody>
            </template>
        </q-markup-table>

        <!-- Step Confirmation Dialog -->
        <q-dialog v-model="confirmDialogVisible" persistent>
            <q-card style="min-width: 500px; max-width: 90vw">
                <q-card-section class="row items-center bg-primary text-white">
                    <div class="text-h6">
                        {{ $t("onboarding.confirm_step_dialog_title") }}
                    </div>
                    <q-space />
                    <q-btn icon="close" flat round dense v-close-popup />
                </q-card-section>

                <q-card-section
                    v-if="
                        selectedPublisher &&
                        selectedPublisher.pendingConfirmationStep
                    "
                    class="q-pa-md"
                >
                    <div class="text-subtitle1 text-weight-bold q-mb-xs">
                        {{ selectedPublisher.name }}
                    </div>
                    <div class="text-subtitle2 text-grey-8 q-mb-md">
                        {{ $t("onboarding.step_title") }}:
                        {{ selectedPublisher.pendingConfirmationStep.title }}
                    </div>

                    <p class="text-body2 text-grey-9 q-mb-md">
                        {{ $t("onboarding.confirm_step_dialog_desc") }}
                    </p>

                    <div
                        v-if="
                            selectedPublisher.pendingConfirmationStep
                                .instructions
                        "
                        class="q-pa-sm bg-grey-2 rounded-borders q-mb-md"
                    >
                        <div class="text-caption text-weight-bold text-grey-7">
                            {{ $t("onboarding.instructions_for_coordinator") }}:
                        </div>
                        <div class="text-body2" style="white-space: pre-wrap">
                            {{
                                selectedPublisher.pendingConfirmationStep
                                    .instructions
                            }}
                        </div>
                    </div>
                </q-card-section>

                <q-card-actions align="right" class="q-pa-md">
                    <q-btn
                        flat
                        :label="$t('button.action.cancel')"
                        color="grey"
                        v-close-popup
                    />
                    <q-btn
                        color="secondary"
                        icon="check"
                        :label="$t('onboarding.confirm_step')"
                        :loading="loading"
                        @click="submitStepConfirmation"
                    />
                </q-card-actions>
            </q-card>
        </q-dialog>

        <!-- Reset Confirmation Dialog -->
        <q-dialog v-model="resetDialogVisible" persistent>
            <q-card style="min-width: 400px; max-width: 90vw">
                <q-card-section class="row items-center bg-negative text-white">
                    <div class="text-h6">
                        {{ $t("onboarding.reset_confirm_title") }}
                    </div>
                    <q-space />
                    <q-btn icon="close" flat round dense v-close-popup />
                </q-card-section>

                <q-card-section v-if="selectedResetPublisher" class="q-pa-md">
                    <div class="text-subtitle1 text-weight-bold q-mb-xs">
                        {{ selectedResetPublisher.name }}
                    </div>
                    <p class="text-body2 text-grey-9 q-mb-md">
                        {{ $t("onboarding.reset_confirm_desc") }}
                    </p>
                </q-card-section>

                <q-card-actions align="right" class="q-pa-md">
                    <q-btn
                        flat
                        :label="$t('button.action.cancel')"
                        color="grey"
                        v-close-popup
                    />
                    <q-btn
                        color="negative"
                        icon="restart_alt"
                        :label="$t('onboarding.reset')"
                        :loading="resetLoading"
                        @click="submitReset"
                    />
                </q-card-actions>
            </q-card>
        </q-dialog>
    </div>
</template>

<script setup>
import { ref, onMounted, getCurrentInstance } from "vue";
import { useQuasar } from "quasar";
import axios from "axios";

const props = defineProps({
    publishers: {
        type: Array,
        required: true,
    },
    selectedUserId: {
        type: [Number, String],
        default: null,
    },
    confirmRouteTemplate: {
        type: String,
        required: true,
    },
    finishRouteTemplate: {
        type: String,
        required: true,
    },
    resetRouteTemplate: {
        type: String,
        default: "",
    },
    csrfToken: {
        type: String,
        default: "",
    },
});

const { proxy } = getCurrentInstance();
const t = (key, replacements) => proxy?.$t(key, replacements) || key;
const $q = useQuasar();

const localPublishers = ref([...props.publishers]);
const confirmDialogVisible = ref(false);
const selectedPublisher = ref(null);
const loading = ref(false);

const resetDialogVisible = ref(false);
const selectedResetPublisher = ref(null);
const resetLoading = ref(false);

const getFinishUrl = (userId) => {
    return props.finishRouteTemplate.replace("__USER_ID__", userId);
};

const getResetUrl = (userId) => {
    return props.resetRouteTemplate
        ? props.resetRouteTemplate.replace("__USER_ID__", userId)
        : "";
};

const openConfirmModal = (pub) => {
    selectedPublisher.value = pub;
    confirmDialogVisible.value = true;
};

const openResetModal = (pub) => {
    selectedResetPublisher.value = pub;
    resetDialogVisible.value = true;
};

const submitStepConfirmation = async () => {
    if (
        !selectedPublisher.value ||
        !selectedPublisher.value.pendingConfirmationStep
    )
        return;

    loading.value = true;
    const userId = selectedPublisher.value.userId;
    const stepId = selectedPublisher.value.pendingConfirmationStep.id;
    const url = props.confirmRouteTemplate
        .replace("__USER_ID__", userId)
        .replace("__STEP_ID__", stepId);

    try {
        const response = await axios.post(url);
        $q.notify({
            type: "positive",
            message:
                response.data.message || t("onboarding.step_confirmed_success"),
        });

        confirmDialogVisible.value = false;
        window.location.reload();
    } catch (e) {
        $q.notify({
            type: "negative",
            message: "Error confirming step",
        });
    } finally {
        loading.value = false;
    }
};

const submitReset = async () => {
    if (!selectedResetPublisher.value) return;

    resetLoading.value = true;
    const userId = selectedResetPublisher.value.userId;
    const url = getResetUrl(userId);

    try {
        const response = await axios.post(url, {
            _token: props.csrfToken,
        });
        $q.notify({
            type: "positive",
            message: response.data.message || t("onboarding.reset_success"),
        });

        resetDialogVisible.value = false;
        window.location.reload();
    } catch (e) {
        $q.notify({
            type: "negative",
            message: "Error resetting onboarding status",
        });
    } finally {
        resetLoading.value = false;
    }
};

onMounted(() => {
    if (props.selectedUserId) {
        const target = localPublishers.value.find(
            (p) => String(p.userId) === String(props.selectedUserId),
        );
        if (target && target.pendingConfirmationStep) {
            openConfirmModal(target);
        }
    }
});
</script>
